import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Gmail's "Archived. Undo", done cheaply.
 *
 * Undoing a move for real needs the message's NEW uid in the destination
 * folder, which SnappyMail's MessageMove does not return. So instead of moving
 * and moving back, the action is simply not sent yet: the rows disappear at
 * once, the server call is held for a few seconds, and Undo cancels it and puts
 * the rows back. Nothing to reverse, because nothing happened.
 *
 * The held action is committed early -- never dropped -- when another action
 * replaces it, or when the page is hidden or closed (switching apps on a phone
 * counts), so leaving the app never silently loses a delete.
 */

const HOLD_MS = 5000;

interface Pending {
	id: number;
	label: string;
	commit: () => Promise<void>;
	restore: () => void;
}

export interface PendingAction {
	/** Text for the toast, e.g. "Archived". null when nothing is pending. */
	label: string | null;
	schedule: (label: string, commit: () => Promise<void>, restore: () => void) => void;
	undo: () => void;
}

export function usePendingAction(onCommitFailed: (label: string) => void): PendingAction {
	const [label, setLabel] = useState<string | null>(null);
	const pending = useRef<Pending | null>(null);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const nextId = useRef(0);
	const failed = useRef(onCommitFailed);
	failed.current = onCommitFailed;

	const flush = useCallback(() => {
		const action = pending.current;
		if (!action) {
			return;
		}
		pending.current = null;
		if (timer.current) {
			clearTimeout(timer.current);
			timer.current = null;
		}
		setLabel(current => (current === action.label ? null : current));
		action.commit().catch(() => {
			action.restore();
			failed.current(action.label);
		});
	}, []);

	const schedule = useCallback(
		(text: string, commit: () => Promise<void>, restore: () => void) => {
			flush();
			const action = { id: ++nextId.current, label: text, commit, restore };
			pending.current = action;
			setLabel(text);
			timer.current = setTimeout(flush, HOLD_MS);
		},
		[flush]
	);

	const undo = useCallback(() => {
		const action = pending.current;
		if (!action) {
			return;
		}
		pending.current = null;
		if (timer.current) {
			clearTimeout(timer.current);
			timer.current = null;
		}
		setLabel(null);
		action.restore();
	}, []);

	useEffect(() => {
		const onHide = () => {
			if ('hidden' === document.visibilityState) {
				flush();
			}
		};
		document.addEventListener('visibilitychange', onHide);
		window.addEventListener('pagehide', flush);
		return () => {
			document.removeEventListener('visibilitychange', onHide);
			window.removeEventListener('pagehide', flush);
			flush();
		};
	}, [flush]);

	return { label, schedule, undo };
}
